import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { ScrollArea } from '../../ui/scroll-area';
import { 
  Receipt, History, Calendar, Download, RefreshCw, 
  Banknote, CreditCard 
} from 'lucide-react';

const CajaModal = ({ 
  cajaResumen, 
  cajaHistorial, 
  showHistorial, 
  downloadingPdf,
  onToggleHistorial, 
  onDownloadPdf, 
  onClose 
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="caja-modal">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Receipt size={24} className="text-emerald-600" />
                {showHistorial ? 'Historial de Caja' : 'Resumen de Caja'}
              </CardTitle>
              <p className="text-sm text-gray-500">{showHistorial ? 'Ultimos 30 dias' : `Hoy: ${cajaResumen.date}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onToggleHistorial}
                className="text-emerald-600"
              >
                {showHistorial ? <Calendar size={16} /> : <History size={16} />}
                <span className="ml-1 hidden sm:inline">{showHistorial ? 'Hoy' : 'Historial'}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDownloadPdf}
                disabled={downloadingPdf}
                className="text-blue-600"
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? <RefreshCw className="animate-spin" size={16} /> : <Download size={16} />}
                <span className="ml-1 hidden sm:inline">PDF</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <ScrollArea className="flex-1">
          <CardContent className="space-y-4 pt-4">
            {!showHistorial ? (
              <>
                <div className="text-center p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                  <p className="text-sm text-gray-600 mb-1">Total Facturado Hoy</p>
                  <p className="text-4xl font-bold text-emerald-600">{cajaResumen.total.amount.toFixed(2)}€</p>
                  <p className="text-sm text-gray-500 mt-1">{cajaResumen.total.count} ventas</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200 text-center">
                    <Banknote size={28} className="text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-700">{cajaResumen.efectivo.total.toFixed(2)}€</p>
                    <p className="text-sm text-green-600 font-medium">Efectivo</p>
                    <p className="text-xs text-gray-500 mt-1">{cajaResumen.efectivo.count} ventas</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-center">
                    <CreditCard size={28} className="text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-700">{cajaResumen.tarjeta.total.toFixed(2)}€</p>
                    <p className="text-sm text-blue-600 font-medium">Tarjeta</p>
                    <p className="text-xs text-gray-500 mt-1">{cajaResumen.tarjeta.count} ventas</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {cajaHistorial.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <History size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No hay datos de dias anteriores</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200 mb-4">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="font-bold text-emerald-600">
                          {cajaHistorial.reduce((sum, d) => sum + d.total.amount, 0).toFixed(2)}€
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Efectivo</p>
                        <p className="font-bold text-green-600">
                          {cajaHistorial.reduce((sum, d) => sum + d.efectivo.total, 0).toFixed(2)}€
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Tarjeta</p>
                        <p className="font-bold text-blue-600">
                          {cajaHistorial.reduce((sum, d) => sum + d.tarjeta.total, 0).toFixed(2)}€
                        </p>
                      </div>
                    </div>
                    
                    {cajaHistorial.map((day) => (
                      <div 
                        key={day.date} 
                        className="p-3 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-400 w-8">{day.day_name}</span>
                            <span className="font-medium text-gray-700">{day.date}</span>
                          </div>
                          <span className="font-bold text-emerald-600">{day.total.amount.toFixed(2)}€</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="flex items-center gap-1 text-green-600">
                            <Banknote size={12} /> {day.efectivo.total.toFixed(2)}€
                          </span>
                          <span className="flex items-center gap-1 text-blue-600">
                            <CreditCard size={12} /> {day.tarjeta.total.toFixed(2)}€
                          </span>
                          <span className="text-gray-400 ml-auto">{day.total.count} ventas</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </ScrollArea>
        
        <div className="p-4 border-t">
          <Button variant="outline" onClick={onClose} className="w-full">
            Cerrar
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default CajaModal;
